import { useCallback, useState } from 'react';

import type { DateRange } from 'react-day-picker';

import { Calendar } from '../../../../general/Calendar';
import { COLORS } from '../../../../types/Colors';
import { getColorClasses } from '../../../../utils/Colors';
import type { EventBehaviorProps } from '../../../../utils/EventBehavior';
import { DROPDOWN_SIZE } from '../../types';
import type { DropdownMenuDatePickerProps } from '../../types';
import { Trigger } from '../shared/Trigger';

export const DatePicker = ({
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className,
    datePickerMode = 'single',
    dateValue,
    eventPreventDefault,
    eventStopPropagation,
    id,
    onDateChange,
    onOpenChange,
    open: controlledOpen,
    placeholder = 'Select...',
    roundedTrigger = false,
    showBorder = true,
    size = DROPDOWN_SIZE.SM,
    triggerClassName,
    triggerWidth,
}: DropdownMenuDatePickerProps & EventBehaviorProps) => {
    const [isDateInputOpen, setIsDateInputOpen] = useState(false);
    const isOpen = controlledOpen ?? isDateInputOpen;

    const handleOpenChange = useCallback(
        (state: boolean) => {
            setIsDateInputOpen(state);
            onOpenChange?.(state);
        },
        [onOpenChange],
    );

    const handleSingleSelect = useCallback(
        (date: Date | undefined) => {
            (onDateChange as ((date: Date | null) => void) | undefined)?.(date ?? null);
            // Auto-close on single date selection
            if (date) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setIsDateInputOpen(false);
                        onOpenChange?.(false);
                    });
                });
            }
        },
        [onDateChange, onOpenChange],
    );

    const calendarProps =
        datePickerMode === 'range'
            ? {
                  defaultMonth: (dateValue as DateRange | undefined)?.from,
                  mode: 'range' as const,
                  onSelect: onDateChange as ((range: DateRange | undefined) => void) | undefined,
                  selected: dateValue as DateRange | undefined,
              }
            : {
                  defaultMonth: (dateValue as Date | null | undefined) ?? undefined,
                  mode: 'single' as const,
                  onSelect: handleSingleSelect,
                  selected: (dateValue as Date | null | undefined) ?? undefined,
              };

    return (
        <div className={className}>
            <Calendar
                {...calendarProps}
                popover
                trigger={
                    <Trigger
                        aria-label={ariaLabel}
                        aria-labelledby={ariaLabelledBy}
                        id={id}
                        isOpen={isOpen}
                        displayValue={placeholder}
                        colorClasses={getColorClasses(COLORS.GREY)}
                        size={size}
                        roundedTrigger={roundedTrigger}
                        showBorder={showBorder}
                        multiple={false}
                        triggerClassName={triggerClassName}
                        triggerWidth={triggerWidth}
                        eventPreventDefault={eventPreventDefault}
                        eventStopPropagation={eventStopPropagation}
                    />
                }
                open={isOpen}
                onOpenChange={handleOpenChange}
            />
        </div>
    );
};
